import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, ScrollView } from 'react-native';
import BasicLayout from '../components/Shared/BasicLayout';
import Constants from '../constants';
import { Navigate } from '../react-router';
import { UserContext } from '../contexts/user';
import Spinner from '../components/Spinner';
import { Button, Theme } from '../components/Button';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function AiTest(): JSX.Element {
  const { state } = React.useContext(UserContext);
  const shouldRedirect = state.user && !state.user.wpAdmin;
  const [question, setQuestion] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    // Scroll to bottom when new messages are added
    if (scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages]);

  const sendQuestion = async () => {
    if (!question.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuestion('');
    setLoading(true);
    setError(null);

    try {
      const token = state.user?.token || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const organizationId = state.user?.organization?.id;
      const url = `${Constants.base_url}/admin/ai-test/${token}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: userMessage.content,
          organization_id: organizationId ? parseInt(organizationId) : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to get response' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || data.answer || data.message || 'No response received',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get AI response';
      setError(errorMessage);
      
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Error: ${errorMessage}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  return (
    <BasicLayout active="/ai-test">
      {shouldRedirect ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.heading}>AI Test</Text>
            <Text style={styles.subtitle}>Test AI responses directly</Text>
            {messages.length > 0 && (
              <Button
                text="Clear Chat"
                onPress={clearChat}
                theme={Theme.LINK_DANGER}
                small={true}
                width={120}
                style={{ marginTop: 10 }}
              />
            )}
          </View>

          <View style={styles.chatContainer}>
            <ScrollView
              ref={scrollViewRef}
              style={styles.messagesContainer}
              contentContainerStyle={styles.messagesContent}
            >
              {messages.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>
                    Start a conversation with the AI by asking a question below.
                  </Text>
                </View>
              ) : (
                messages.map((message) => (
                  <View
                    key={message.id}
                    style={[
                      styles.message,
                      message.role === 'user' ? styles.userMessage : styles.assistantMessage,
                    ]}
                  >
                    <View style={styles.messageHeader}>
                      <Text style={message.role === 'user' ? styles.userMessageRole : styles.messageRole}>
                        {message.role === 'user' ? 'You' : 'AI'}
                      </Text>
                      <Text style={[styles.messageTime, message.role === 'user' && { color: 'rgba(255, 255, 255, 0.8)' }]}>
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.messageContent,
                        message.role === 'user' ? styles.userMessageContent : null,
                        message.role === 'assistant' && message.content.startsWith('Error:')
                          ? styles.errorMessage
                          : null,
                      ]}
                    >
                      {message.content}
                    </Text>
                  </View>
                ))
              )}
              {loading && (
                <View style={[styles.message, styles.assistantMessage]}>
                  <View style={styles.messageHeader}>
                    <Text style={styles.messageRole}>AI</Text>
                  </View>
                  <View style={styles.loadingContainer}>
                    <Spinner />
                    <Text style={styles.loadingText}>Thinking...</Text>
                  </View>
                </View>
              )}
            </ScrollView>

            <View style={styles.inputContainer}>
              <TextInput
                style={styles.input}
                placeholder="Type your question here..."
                value={question}
                onChangeText={setQuestion}
                onKeyPress={handleKeyPress}
                multiline
                editable={!loading}
                placeholderTextColor={Constants.colors.info}
              />
              <Button
                text="Send"
                onPress={sendQuestion}
                disabled={!question.trim() || loading}
                loading={loading}
                theme={Theme.PRIMARY}
                width={100}
                style={styles.sendButton}
              />
            </View>
          </View>
        </View>
      )}
    </BasicLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 25,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginBottom: 20,
  },
  heading: {
    fontSize: 32,
    fontFamily: 'GothamBold',
    color: Constants.colors.blue,
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
  },
  chatContainer: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#102A9C',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 500,
  },
  messagesContainer: {
    flex: 1,
    marginBottom: 15,
  },
  messagesContent: {
    paddingBottom: 10,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 16,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
    textAlign: 'center',
  },
  message: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 8,
    maxWidth: '85%',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: Constants.colors.blue,
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    backgroundColor: Constants.colors.lightGrey,
  },
  messageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  messageRole: {
    fontSize: 12,
    fontFamily: 'GothamBold',
    color: Constants.colors.darkBlue,
  },
  userMessageRole: {
    fontSize: 12,
    fontFamily: 'GothamBold',
    color: '#fff',
  },
  messageTime: {
    fontSize: 10,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
    marginLeft: 8,
  },
  messageContent: {
    fontSize: 14,
    fontFamily: 'Gotham',
    color: Constants.colors.dark,
    lineHeight: 20,
  },
  userMessageContent: {
    color: '#fff',
  },
  errorMessage: {
    color: Constants.colors.red,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  loadingText: {
    fontSize: 14,
    fontFamily: 'Gotham',
    color: Constants.colors.info,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 10,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: `${Constants.colors.info}33`,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: `${Constants.colors.info}55`,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: 'Gotham',
    fontSize: 14,
    minHeight: 44,
    maxHeight: 120,
    textAlignVertical: 'top',
  },
  sendButton: {
    marginBottom: 0,
  },
});

